import { useEffect, useRef } from "react";

// Keys that the Ant Select owns: navigation through options, selection,
// dismissal, and focus handoff. We must let these bubble up so keyboard users
// can still drive the combobox. Everything else (letters, digits, backspace…)
// is stopped so typing reaches our search input instead of the Select's
// built-in type-ahead.
const SELECT_OWNED_KEYS = new Set([
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Enter",
    "Escape",
    "Tab",
    "Home",
    "End",
    "PageUp",
    "PageDown",
]);

interface CountrySearchPopupProps {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    children: React.ReactNode;
    ariaLabel?: string;
    wrapperClassName?: string;
    inputClassName?: string;
}

/**
 * Search input + menu wrapper used inside Ant Select's popupRender across
 * every country/phone-code picker. Handles:
 *   - auto-focus each time the popup becomes visible (via IntersectionObserver,
 *     since antd keeps popupRender content mounted but hidden between opens
 *     and does NOT re-render it while hidden — so prop-driven focus can't work)
 *   - synthetic mouseDown before focus to prevent antd 6's lockOptions from
 *     freezing the option list (rc-select locks options when the Select's
 *     internal input blurs; the mouseDown sets internalMouseDownRef which
 *     tells the blur handler to skip the lock)
 *   - onMouseDown guard: stop propagation only when the user clicks the
 *     input, so the Select doesn't steal focus; option clicks still bubble
 *   - onKeyDown guard: stop character keys from reaching the Select's
 *     type-ahead while letting Arrow/Enter/Escape/Tab through for native
 *     combobox navigation
 */
const CountrySearchPopup: React.FC<CountrySearchPopupProps> = ({
    value,
    onChange,
    placeholder,
    children,
    ariaLabel,
    wrapperClassName = "country-code-options-wrapper",
    inputClassName = "country-search-input mb-8",
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setTimeout(() => {
                        wrapperRef.current?.dispatchEvent(
                            new MouseEvent("mousedown", { bubbles: true }),
                        );
                        inputRef.current?.focus({ preventScroll: true });
                    }, 0);
                }
            },
            { threshold: 0.1 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={wrapperRef}
            className={wrapperClassName}
            onMouseDown={(e) => {
                if ((e.target as HTMLElement).tagName === "INPUT") {
                    e.stopPropagation();
                }
            }}
        >
            <input
                ref={inputRef}
                role="searchbox"
                aria-autocomplete="list"
                className={inputClassName}
                placeholder={placeholder}
                aria-label={ariaLabel ?? placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (!SELECT_OWNED_KEYS.has(e.key)) {
                        e.stopPropagation();
                    }
                }}
            />
            {children}
        </div>
    );
};

export default CountrySearchPopup;
